import type { ArenaSample, ArenaVote, RankingLevel, RankingRow } from "./types";

const BASE_RATING = 1000;
const K_FACTOR = 32;

type MutableRow = Omit<RankingRow, "rank"> & {
  rating: number;
};

export function buildRankings(
  samples: ArenaSample[],
  votes: ArenaVote[],
  level: RankingLevel,
): RankingRow[] {
  const sampleById = new Map(samples.map((sample) => [sample.id, sample]));
  const rows = new Map<string, MutableRow>();

  const ensureRow = (sample: ArenaSample): MutableRow => {
    const key = entityKey(sample, level);
    const existing = rows.get(key);
    if (existing) return existing;
    const row: MutableRow = {
      entityId: key,
      label: entityLabel(sample, level),
      provider: sample.provider,
      model: sample.model,
      voiceName: level === "providerModel" ? undefined : sample.voiceName,
      pricing: pricingLabel(sample.provider),
      language: level === "voiceLanguage" ? sample.language : undefined,
      rating: BASE_RATING,
      wins: 0,
      losses: 0,
      comparisons: 0,
    };
    rows.set(key, row);
    return row;
  };

  for (const sample of samples) ensureRow(sample);

  for (const vote of [...votes].sort((a, b) => a.timestamp.localeCompare(b.timestamp) || a.id.localeCompare(b.id))) {
    if (vote.result === "skip") continue;
    const left = sampleById.get(vote.leftSampleId);
    const right = sampleById.get(vote.rightSampleId);
    if (!left || !right) continue;
    const leftRow = ensureRow(left);
    const rightRow = ensureRow(right);
    if (leftRow.entityId === rightRow.entityId) continue;
    leftRow.comparisons += 1;
    rightRow.comparisons += 1;
    if (vote.result !== "left" && vote.result !== "right") continue;
    const leftScore = vote.result === "left" ? 1 : 0;
    updateElo(leftRow, rightRow, leftScore);
    if (leftScore === 1) {
      leftRow.wins += 1;
      rightRow.losses += 1;
    } else {
      leftRow.losses += 1;
      rightRow.wins += 1;
    }
  }

  return [...rows.values()]
    .filter((row) => row.comparisons > 0)
    .sort((a, b) => b.rating - a.rating || b.comparisons - a.comparisons)
    .map((row, index) => ({ ...row, rank: index + 1, rating: Math.round(row.rating) }));
}

function updateElo(left: MutableRow, right: MutableRow, leftScore: number): void {
  const expectedLeft = 1 / (1 + 10 ** ((right.rating - left.rating) / 400));
  const expectedRight = 1 - expectedLeft;
  right.rating += K_FACTOR * ((1 - leftScore) - expectedRight);
  left.rating += K_FACTOR * (leftScore - expectedLeft);
}

function entityKey(sample: ArenaSample, level: RankingLevel): string {
  const providerModel = `${sample.provider}/${sample.model}`;
  if (level === "providerModel") return providerModel;
  const voice = `${providerModel}/${sample.voiceId}`;
  if (level === "voice") return voice;
  return `${voice}/${sample.language}`;
}

function entityLabel(sample: ArenaSample, level: RankingLevel): string {
  if (level === "providerModel") return `${sample.provider} ${sample.model}`;
  return `${sample.provider} ${sample.voiceName}`;
}

function pricingLabel(provider: string): string {
  const pricing: Record<string, string> = {
    gemini: "TBD",
    inworld: "TBD",
    sarvam: "TBD",
    stepaudio: "TBD",
    cartesia: "TBD",
    elevenlabs: "TBD",
    openai: "TBD",
  };
  return pricing[provider] ?? "TBD";
}
